import { datadogRum } from "@datadog/browser-rum";
import { Serialize } from "@trpc/server/unstable-core-do-not-import";
import React, {
  ReactElement,
  useState,
  useEffect,
  useContext,
  createContext,
  useMemo,
  useCallback,
} from "react";
import { UserProfile } from "server/schemas/user";
import { isPathServedFromZapierCore } from "utils/isPathServedFromZapierCore";
import { isPublishedPageHost } from "utils/isPublishedPageHost";
import { zrpc } from "@/lib/zrpc";
import { useQueryClient } from "@tanstack/react-query";
import { isApiUnauthorizedError } from "@/server/openapi/errors";

export type ClientUserProfile = Serialize<UserProfile>;

export type UserContext = {
  user?: ClientUserProfile;
  error?: Error;
  isLoading: boolean;
  syncSession: () => Promise<void>;
};

export type UserProviderProps = React.PropsWithChildren<{
  fallback?: React.ReactNode;
  user?: ClientUserProfile;
}>;

const missingUserProvider = "You forgot to wrap your app in <UserProvider>";

export const UserContext = createContext<UserContext>({
  get user(): never {
    throw new Error(missingUserProvider);
  },
  get error(): never {
    throw new Error(missingUserProvider);
  },
  get isLoading(): never {
    throw new Error(missingUserProvider);
  },
  syncSession: (): never => {
    throw new Error(missingUserProvider);
  },
});

export type UseUser = () => UserContext;

export const useUser: UseUser = () => useContext<UserContext>(UserContext);

export type UserProvider = (
  props: UserProviderProps
) => ReactElement<UserContext>;

type UserProviderState = {
  user?: ClientUserProfile;
  error?: Error;
  isLoading: boolean;
};

export default function UserProvider({
  children,
  fallback,
  user: initialUser,
}: UserProviderProps): ReactElement<UserContext> {
  const [isNotEditorPage, setIsNotEditorPage] = useState<boolean>(true);

  const [state, setState] = useState<UserProviderState>({
    user: initialUser,
    isLoading: !initialUser,
  });

  const shouldSkipUserFetching = useMemo(
    () => isNotEditorPage || state.user,
    [isNotEditorPage, state.user]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isPublishedPage = isPublishedPageHost({ host: window.location.host });
    const isPageServedFromZapierCore = isPathServedFromZapierCore(
      window.location.pathname
    );

    const isNotEditorPage = isPublishedPage || isPageServedFromZapierCore;

    setIsNotEditorPage(isNotEditorPage);

    // If it's a project page, we won't fetch the user, so set isLoading to false
    if (isNotEditorPage) {
      setState((previous) => ({ ...previous, isLoading: false }));
    }
  }, [setIsNotEditorPage, setState]);

  const setUser = useCallback((user: ClientUserProfile) => {
    datadogRum.setUser(user);
    setState((previous) => ({
      ...previous,
      user,
      error: undefined,
      isLoading: false,
    }));
    localStorage.setItem("currentAccountId", user.currentAccount.toString());
    localStorage.setItem("customUserId", user.zapierId.toString());
  }, []);

  const setError = useCallback((error: Error) => {
    setState((previous) => ({
      ...previous,
      user: undefined,
      error,
      isLoading: false,
    }));
  }, []);

  const onSuccess = useCallback(
    (data?: ClientUserProfile) => {
      if (data) {
        setUser(data);
      }
    },
    [setUser]
  );

  const onError = useCallback(
    (error: unknown) => {
      if (isApiUnauthorizedError(error)) {
        // Redirecting to login will be handled by `withPageAuthRequired` hook.
        setState((previous) => ({
          ...previous,
          user: undefined,
          isLoading: false,
        }));
      } else {
        setError(new Error("Could not fetch current user"));
      }
    },
    [setError]
  );

  const queryClient = useQueryClient();
  const { refetch: getUser, isPending: initialCurrentUserCallLoading } =
    zrpc.interfaces.useQuery(
      "get",
      "/api/interfaces/v0/internal/auth/current-user",
      {},
      {
        enabled: false,
        throwOnError: false,
        meta: { noToast: true },
      }
    );

  useEffect((): void => {
    if (shouldSkipUserFetching) {
      return;
    }

    /**
     * The `throwOnError` here is important.
     * Without it, the `.catch` would never fire if the request fails.
     *
     * Please note that setting `throwOnError: true` OR removing the `throwOnError` from the `useQuery`
     * will NOT have the same effect as setting the `throwOnError` here.
     */
    void getUser({ throwOnError: true })
      .then(({ data: currentUserData }) => {
        onSuccess(currentUserData);
      })
      .catch((currentUserError) => {
        onError(currentUserError);
      });
  }, [getUser, onSuccess, shouldSkipUserFetching, onError]);

  // This call happens in the background but will ensure the local user is
  // refreshed from the monolith.
  const {
    mutate: syncCurrentUser,
    isSuccess: userHasBeenSynced,
    isPending: syncingCurrentUser,
    failureCount: syncCurrentUserFailureCount,
  } = zrpc.interfaces.useMutation(
    "post",
    "/api/interfaces/v0/internal/auth/sync-current-user",
    {
      meta: { noToast: true },
      throwOnError: false,

      onError: (syncCurrentUserError) => {
        onError(syncCurrentUserError);
      },
      onSuccess: (userData) => {
        onSuccess(userData);

        const projectsListingQueryOptions = zrpc.interfaces.queryOptions(
          "get",
          "/api/interfaces/v0/list-listing-page"
        );
        void queryClient.invalidateQueries({
          queryKey: projectsListingQueryOptions.queryKey,
          refetchType: "all",
        });

        const countQueryOptions = zrpc.interfaces.queryOptions(
          "get",
          "/api/interfaces/v0/interfaces/count"
        );
        void queryClient.invalidateQueries(countQueryOptions);
      },
    }
  );

  useEffect((): void => {
    if (shouldSkipUserFetching) {
      return;
    }

    if (initialCurrentUserCallLoading) {
      return;
    }

    if (userHasBeenSynced) {
      return;
    }

    if (syncingCurrentUser) {
      return;
    }

    /**
     * This ensures we do not fall into an infinite loop of syncing the user.
     * If it failed once, it will fail again. When it fails, it will trigger the login redirect flow.
     */
    if (syncCurrentUserFailureCount > 0) {
      return;
    }

    syncCurrentUser(undefined);
  }, [
    syncCurrentUser,
    shouldSkipUserFetching,
    initialCurrentUserCallLoading,
    userHasBeenSynced,
    syncingCurrentUser,
    syncCurrentUserFailureCount,
  ]);

  const { user, error, isLoading } = state;

  return (
    <UserContext.Provider
      value={{
        user,
        error,
        isLoading,
        syncSession: async () => {
          syncCurrentUser(undefined);
        },
      }}
    >
      {isLoading && fallback ? fallback : children}
    </UserContext.Provider>
  );
}
